import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import useTaskCompletion from '@/hooks/useTaskCompletion';
import { botRunsAPI, projectsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Copy, FloppyDisk } from '@phosphor-icons/react';
import TaskExecutionMeta from '@/components/TaskExecutionMeta';

const GrowthBotPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const { completeIfDeepLinked } = useTaskCompletion();
  const [step, setStep] = useState('input');
  const [status, setStatus] = useState('pending');
  const [runId, setRunId] = useState(null);
  const [formData, setFormData] = useState({
    niche: '',
    platform: 'Instagram',
    time_per_day: '1-2 hours',
    posting_frequency: 'daily',
    level: 'beginner',
  });
  const [outputs, setOutputs] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep('running');
    setStatus('running');

    try {
      const response = await botRunsAPI.create({
        bot_type: 'growth',
        inputs: formData,
      });

      setRunId(response.data.id);
      setOutputs(response.data.outputs);
      setStatus('completed');
      
      setTimeout(() => {
        setStep('result');
      }, 1000);
      
      toast.success('Growth plan generated!');
    } catch (error) {
      setStatus('failed');
      toast.error('Failed to generate growth plan.');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleSave = async () => {
    const projectName = prompt('Enter a name for this project:');
    if (!projectName) return;

    setSaving(true);
    try {
      const res = await projectsAPI.create({
        project_name: projectName,
        bot_run_id: runId,
      });
      toast.success('Saved to Projects!');
      // Round 96 — close the Round-94 workflow loop.
      await completeIfDeepLinked(
        { asset_id: res?.data?.id || runId, saved_at: new Date().toISOString() },
      );
    } catch (error) {
      toast.error('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setStep('input');
    setStatus('pending');
    setOutputs(null);
    setRunId(null);
  };

  // Round 95 — deep-link acceptance from <TaskActionButton />.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[GrowthBot] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-4 flex items-center justify-between">
          <ReturnToDashboardPill testId="growth-bot-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="growth-bot-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="growth-bot-title">
            Growth Bot
          </h1>
          <p className="text-slate-600">
            Create a 30-day content calendar and growth strategy
          </p>
        </div>

        {step === 'input' && (
          <form onSubmit={handleSubmit} data-testid="growth-bot-form" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your Niche *</label>
              <input
                type="text"
                name="niche"
                required
                data-testid="growth-niche-input"
                value={formData.niche}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g., fitness, cooking, tech"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Platform *</label>
              <select
                name="platform"
                data-testid="growth-platform-select"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="Facebook">Facebook</option>
                <option value="YouTube">YouTube</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Time Available Per Day *</label>
              <select
                name="time_per_day"
                data-testid="growth-time-select"
                value={formData.time_per_day}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="30 minutes">30 minutes</option>
                <option value="1-2 hours">1-2 hours</option>
                <option value="2-4 hours">2-4 hours</option>
                <option value="4+ hours">4+ hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Posting Frequency *</label>
              <select
                name="posting_frequency"
                data-testid="growth-frequency-select"
                value={formData.posting_frequency}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="daily">Daily</option>
                <option value="3-4 times per week">3-4 times per week</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your Level *</label>
              <select
                name="level"
                data-testid="growth-level-select"
                value={formData.level}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
              </select>
            </div>

            <button
              type="submit"
              data-testid="growth-bot-submit"
              className="w-full py-4 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
            >
              Generate Growth Plan
            </button>
          </form>
        )}

        {step === 'running' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-6" data-testid="growth-bot-running">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-slate-900">Building Your Growth Plan...</h3>
            </div>
            <ProgressBar status={status} />
          </div>
        )}

        {step === 'result' && outputs && (
          <div className="space-y-6" data-testid="growth-bot-results">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-project-button"
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              >
                <FloppyDisk size={20} weight="duotone" />
                <span>{saving ? 'Saving...' : 'Save to Projects'}</span>
              </button>
              <button onClick={handleReset} data-testid="run-another-button" className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-medium border border-slate-200 rounded-lg">
                Run Another
              </button>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">30-Day Content Calendar</h3>
              <div className="space-y-2">
                {outputs.calendar.map((day, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid={`growth-calendar-day-${day.day || index + 1}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-slate-900">{day.topic}</p>
                        <p className="text-sm text-slate-600">{day.content_type} • {day.focus}</p>
                      </div>
                      {day.day && (
                        <span className="text-xs text-slate-500">Day {day.day}</span>
                      )}
                    </div>
                    {/* Round 34 — tier-correct execution metadata */}
                    {(day.where_to_use || day.tier_asset) && (
                      <TaskExecutionMeta
                        action={day.next_recommended_action}
                        whereToUse={day.where_to_use}
                        tierAsset={day.tier_asset}
                        testIdPrefix={`growth-day-${day.day || index + 1}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Posting Schedule</h3>
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{outputs.posting_schedule}</pre>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Growth Tips</h3>
              <ul className="space-y-2">
                {outputs.growth_tips.map((tip, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span className="text-slate-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 space-y-4">
              <h3 className="text-xl font-semibold text-slate-900">Weekly Goals</h3>
              <ul className="space-y-3">
                {outputs.weekly_goals.map((goal, index) => (
                  <li key={index} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    {typeof goal === 'object' ? (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-emerald-800">Week {goal.week}: {goal.goal}</span>
                        </div>
                        {goal.focus && (
                          <p className="text-emerald-700 text-sm mb-2">{goal.focus}</p>
                        )}
                        {goal.metrics && (
                          <p className="text-emerald-600 text-xs bg-emerald-100 px-2 py-1 rounded inline-block">
                            Metrics: {goal.metrics}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-emerald-900">{goal}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthBotPage;
